import { GoogleMap } from '@react-google-maps/api';
import { Theme, css } from '@emotion/react';
import { map as _map, isEmpty } from 'es-toolkit/compat';
import { useState } from 'react';

import {
  BusinessEntity,
  ConsumerPurchaseSummary,
  PatronEntity,
  TicketTypeEntity,
} from 'generated/graphql';
import { LocationPurchaseHistory } from 'selectors/locationGroupedPurchaseHistorySelector';
import { getLatLong } from 'utils/locationUtils';
import { isTouchScreen } from 'utils/deviceUtils';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';
import FullscreenControl from 'components/profile/culinary-travel-log/FullscreenControl';
import ProfileMapNew from 'components/profile/culinary-travel-log/ProfileMapNew';
import RestaurantMarker from 'components/profile/culinary-travel-log/RestaurantMarker';
import ZoomControls from 'components/profile/culinary-travel-log/ZoomControls';
import mapStyle from 'components/profile/culinary-travel-log/mapStyle';

interface Props {
  locationPurchaseHistory: LocationPurchaseHistory[];
  patronZip?: string;
  patronCity?: string;
  isMapLoaded: boolean;
  loadError?: Error;
}

function ProfileMap({
  locationPurchaseHistory,
  patronZip,
  patronCity,
  isMapLoaded,
  loadError,
}: Props) {
  const visglEnabled = useIsVisglMapsEnabled();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | undefined>(undefined);
  const isTouch = isTouchScreen();

  // Weirdly, replacing this with a literal causes strange map behavior on pin selection
  const [center] = useState({ lat: 44.076613, lng: -98.362239833 });

  const handleOpen = (markerId: string) => {
    setSelectedMarkerId(undefined);
    setSelectedMarkerId(markerId);
  };

  const handleClose = (markerId: string) => {
    if (selectedMarkerId === markerId) {
      setSelectedMarkerId(undefined);
    }
  };

  const handleMapClick = () => {
    setSelectedMarkerId(undefined);
  };

  async function fitMapToLocations(map: google.maps.Map) {
    const boundingBox = new google.maps.LatLngBounds();
    locationPurchaseHistory.forEach(({ location }) => {
      boundingBox.extend(location);
    });

    if (locationPurchaseHistory.length === 1) {
      const { lat, lng } = locationPurchaseHistory[0].location;
      // without manually zooming out, 1 location gets zoomed to the building level
      boundingBox.extend(new google.maps.LatLng(lat + 0.025, lng + 0.025));
      boundingBox.extend(new google.maps.LatLng(lat - 0.025, lng - 0.025));
    }

    if (isEmpty(locationPurchaseHistory)) {
      // if present, use patron zipcode to center map
      // else if present, use patron city to center map
      const address = !isEmpty(patronZip)
        ? patronZip
        : !isEmpty(patronCity)
          ? patronCity
          : undefined;
      if (address) {
        const geocodedZip = await getLatLong(address);
        const viewport = geocodedZip.geometry.viewport;
        boundingBox.extend(viewport.getNorthEast());
        boundingBox.extend(viewport.getSouthWest());
      } else {
        // else manually center around the united states
        boundingBox.extend(new google.maps.LatLng(50, -125));
        boundingBox.extend(new google.maps.LatLng(30, -67.5));
      }
    }
    map.fitBounds(boundingBox);
  }

  const fullScreenControlsContainer = (
    <div
      css={({ spacing }: Theme) => css`
        position: absolute;
        top: 0;

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {map ? <FullscreenControl element={map.getDiv() as HTMLElement} /> : null}
    </div>
  );

  const zoomControlsContainer = (
    <div
      css={({ spacing }: Theme) => css`
        position: absolute;
        left: 0;
        bottom: ${spacing(3)}px;

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {map ? <ZoomControls map={map} /> : null}
    </div>
  );

  const onMapLoad = (loadedMap: google.maps.Map | undefined) => {
    if (!loadedMap) {
      return;
    }
    fitMapToLocations(loadedMap);
    setMap(loadedMap);
  };

  // TODO(visgl-burn-in): remove this flag fork and the legacy `<GoogleMap>` branch
  // below (keep ProfileMapNew) once CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully
  // rolled out.
  if (visglEnabled) {
    return (
      <ProfileMapNew
        locationPurchaseHistory={locationPurchaseHistory}
        patronZip={patronZip}
        patronCity={patronCity}
        isMapLoaded={isMapLoaded}
        loadError={loadError}
      />
    );
  }

  if (!isMapLoaded) {
    return <div />;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }

  return (
    <div className="ProfileMap" role="application">
      <GoogleMap
        onLoad={onMapLoad}
        onClick={handleMapClick}
        options={{
          clickableIcons: false,
          disableDefaultUI: true,
          scrollwheel: false,
          styles: mapStyle,
          keyboardShortcuts: false,
        }}
        zoom={12}
        center={center}
        mapContainerClassName="ProfileMap-container"
      >
        {fullScreenControlsContainer}
        {!isTouch ? zoomControlsContainer : null}
        {_map(
          locationPurchaseHistory,
          ({
            location: baseLocation,
            business,
            purchases,
            indexWithinLocation,
          }: LocationPurchaseHistory) => {
            const location = offsetByDistance(baseLocation, 5 * indexWithinLocation, 45);
            const id = `${location.lat},${location.lng}`;
            const consumerPurchases: ConsumerPurchaseSummary[] = _map(purchases, (purchase) => {
              return {
                business: purchase.business as BusinessEntity,
                city: purchase.city as string,
                country: purchase.country as string,
                dinerPatron: purchase.dinerPatron[0] as unknown as PatronEntity,
                firstTransferredTo: purchase.firstTransferredTo as PatronEntity,
                id: purchase.id,
                cancelledOrRefunded: purchase.cancelledOrRefunded,
                eligibleForFeedback: purchase.isEligibleForFeedback,
                ownerPatron: purchase.ownerPatron as PatronEntity,
                ticketCount: purchase.ticketCount,
                ticketDateTime: purchase.ticketDateTime,
                ticketType: purchase.ticketType as TicketTypeEntity,
                cancellationPolicyDescription: [],
                seatingAreaName: '',
                confirmationText: '',
                confirmationCode: '',
              };
            });
            return [
              <RestaurantMarker
                id={id}
                isOpen={selectedMarkerId === id}
                location={location}
                onOpen={handleOpen}
                onClose={handleClose}
                business={business}
                purchases={consumerPurchases}
                key={id}
              />,
            ];
          }
        )}
      </GoogleMap>
    </div>
  );
}

function offsetByDistance(
  latLng: google.maps.LatLngLiteral,
  distanceMeters: number,
  bearingDeg: number
): google.maps.LatLngLiteral {
  // Ported from https://jsfiddle.net/kodisha/8a3hcjtd/
  const R = 6378100; // Radius of the Earth in meters
  const brng = (bearingDeg * Math.PI) / 180; // Convert bearing to radian
  let lat = (latLng.lat * Math.PI) / 180; // Current coords to radians
  let lon = (latLng.lng * Math.PI) / 180;

  lat = Math.asin(
    Math.sin(lat) * Math.cos(distanceMeters / R) +
      Math.cos(lat) * Math.sin(distanceMeters / R) * Math.cos(brng)
  );
  lon += Math.atan2(
    Math.sin(brng) * Math.sin(distanceMeters / R) * Math.cos(lat),
    Math.cos(distanceMeters / R) - Math.sin(lat) * Math.sin(lat)
  );

  return { lat: (lat * 180) / Math.PI, lng: (lon * 180) / Math.PI };
}

export default ProfileMap;
